/**
 * client/viewmodel/animmap.ts — which viewmodel / clip / playback rate a PlayerState asks for (pure, no THREE).
 *
 * OWNER: VIEWMODEL+CAMERA agent. Sources:
 *  - Viewmodel weapon: T5 bgame/bg_weapons.cpp:220 BG_GetViewmodelWeaponIndex — offhand states show the offhand
 *    (hatchet); MELEE_INIT/MELEE_FIRE with a weapon that is not useAsMelee show ps.meleeWeapon (knife_mp), and
 *    MELEE_END is back on ps.weapon (which then quick-raises, T5 PM_Weapon_MeleeEnd) [WPN §5].
 *  - Slots: ps.weaponAnim = WeapAnim | WEAPANIM_TOGGLEBIT; WeapAnim ids are the weapon-file slots of
 *    public/assets/anims/vm_*.json (shared/types.ts). Missing slots fall back like the engine (empty variant ->
 *    normal variant -> idle; T5 cg_weapons.cpp WeaponRunXModelAnims plays PlayIdleAnim when a slot is empty).
 *  - Rates: T5 cgame/cg_weapons.cpp GetWeaponAnimRate + g_animRateOffsets: an anim with a matching weapon time
 *    field plays at rate = clipLength / time (so it lines up with the state machine); fire / last shot / ads
 *    fire / idle play at rate 1. Crawl rates (T6-only anims) ESTIMATE: same rule with crawl*Time.
 *  - Rocket model (ballistic knife blade): T5 cg_weapons.cpp ViewmodelRocketShouldBeAttached.
 */
import { WEAPANIM_TOGGLEBIT, WeapAnim, WeaponId, WeaponState, type PlayerState } from '@shared/types';
import type { WeaponDef } from '@shared/weapons/defs';

export const WEAPANIM_MASK = WEAPANIM_TOGGLEBIT - 1;

/** WeapAnim -> weapon-file slot name (as in vm_*.json `slots`). */
export const WEAPANIM_SLOT: Readonly<Record<number, string>> = {
  [WeapAnim.IDLE]: 'idleAnim',
  [WeapAnim.EMPTY_IDLE]: 'emptyIdleAnim',
  [WeapAnim.FIRE]: 'fireAnim',
  [WeapAnim.LAST_SHOT]: 'lastShotAnim',
  [WeapAnim.HOLD_FIRE]: 'holdFireAnim',
  [WeapAnim.RELOAD]: 'reloadAnim',
  [WeapAnim.RELOAD_EMPTY]: 'reloadEmptyAnim',
  [WeapAnim.RAISE]: 'raiseAnim',
  [WeapAnim.DROP]: 'dropAnim',
  [WeapAnim.FIRST_RAISE]: 'firstRaiseAnim',
  [WeapAnim.ALT_RAISE]: 'altRaiseAnim',
  [WeapAnim.ALT_DROP]: 'altDropAnim',
  [WeapAnim.QUICK_RAISE]: 'quickRaiseAnim',
  [WeapAnim.QUICK_DROP]: 'quickDropAnim',
  [WeapAnim.EMPTY_RAISE]: 'emptyRaiseAnim',
  [WeapAnim.EMPTY_DROP]: 'emptyDropAnim',
  [WeapAnim.SPRINT_IN]: 'sprintInAnim',
  [WeapAnim.SPRINT_LOOP]: 'sprintLoopAnim',
  [WeapAnim.SPRINT_OUT]: 'sprintOutAnim',
  [WeapAnim.SPRINT_IN_EMPTY]: 'sprintInEmptyAnim',
  [WeapAnim.SPRINT_LOOP_EMPTY]: 'sprintLoopEmptyAnim',
  [WeapAnim.SPRINT_OUT_EMPTY]: 'sprintOutEmptyAnim',
  [WeapAnim.CRAWL_IN]: 'crawlInAnim',
  [WeapAnim.CRAWL_FORWARD]: 'crawlForwardAnim',
  [WeapAnim.CRAWL_BACK]: 'crawlBackAnim',
  [WeapAnim.CRAWL_RIGHT]: 'crawlRightAnim',
  [WeapAnim.CRAWL_LEFT]: 'crawlLeftAnim',
  [WeapAnim.CRAWL_OUT]: 'crawlOutAnim',
  [WeapAnim.ADS_FIRE]: 'adsFireAnim',
  [WeapAnim.ADS_LAST_SHOT]: 'adsLastShotAnim',
  [WeapAnim.ADS_UP]: 'adsUpAnim',
  [WeapAnim.ADS_DOWN]: 'adsDownAnim',
  [WeapAnim.MELEE]: 'meleeAnim',
  [WeapAnim.MELEE_CHARGE]: 'meleeChargeAnim',
  [WeapAnim.DTP_IN]: 'dtp_in',
  [WeapAnim.DTP_LOOP]: 'dtp_loop',
  [WeapAnim.DTP_OUT]: 'dtp_out',
};

/** Clip-empty variants of slots (crawl / dtp have no WeapAnim of their own: the clip state picks them). */
const EMPTY_VARIANT: Readonly<Record<string, string>> = {
  crawlInAnim: 'crawlEmptyInAnim', crawlForwardAnim: 'crawlEmptyForwardAnim', crawlBackAnim: 'crawlEmptyBackAnim',
  crawlRightAnim: 'crawlEmptyRightAnim', crawlLeftAnim: 'crawlEmptyLeftAnim', crawlOutAnim: 'crawlEmptyOutAnim',
  dtp_in: 'dtp_empty_in', dtp_loop: 'dtp_empty_loop', dtp_out: 'dtp_empty_out',
};

/** Fallback slot when a weapon file leaves a slot empty (then IDLE). */
const FALLBACK: Readonly<Record<number, number>> = {
  [WeapAnim.EMPTY_IDLE]: WeapAnim.IDLE,
  [WeapAnim.LAST_SHOT]: WeapAnim.FIRE,
  [WeapAnim.ADS_FIRE]: WeapAnim.FIRE,
  [WeapAnim.ADS_LAST_SHOT]: WeapAnim.LAST_SHOT,
  [WeapAnim.HOLD_FIRE]: WeapAnim.IDLE,
  [WeapAnim.RELOAD_EMPTY]: WeapAnim.RELOAD,
  [WeapAnim.FIRST_RAISE]: WeapAnim.RAISE,
  [WeapAnim.ALT_RAISE]: WeapAnim.RAISE,
  [WeapAnim.QUICK_RAISE]: WeapAnim.RAISE,
  [WeapAnim.EMPTY_RAISE]: WeapAnim.RAISE,
  [WeapAnim.ALT_DROP]: WeapAnim.DROP,
  [WeapAnim.QUICK_DROP]: WeapAnim.DROP,
  [WeapAnim.EMPTY_DROP]: WeapAnim.DROP,
  [WeapAnim.SPRINT_IN_EMPTY]: WeapAnim.SPRINT_IN,
  [WeapAnim.SPRINT_LOOP_EMPTY]: WeapAnim.SPRINT_LOOP,
  [WeapAnim.SPRINT_OUT_EMPTY]: WeapAnim.SPRINT_OUT,
  [WeapAnim.MELEE_CHARGE]: WeapAnim.MELEE,
};

/** Slots that loop (idle / loops) regardless of the xanim's looped flag (T5 anim tree: idles loop). */
const LOOPING = new Set<number>([
  WeapAnim.IDLE, WeapAnim.EMPTY_IDLE, WeapAnim.SPRINT_LOOP, WeapAnim.SPRINT_LOOP_EMPTY, WeapAnim.CRAWL_FORWARD,
  WeapAnim.CRAWL_BACK, WeapAnim.CRAWL_LEFT, WeapAnim.CRAWL_RIGHT, WeapAnim.DTP_LOOP,
]);

export function isLoopingAnim(anim: number): boolean {
  return LOOPING.has(anim & WEAPANIM_MASK);
}
export function isIdleAnim(anim: number): boolean {
  const a = anim & WEAPANIM_MASK;
  return a === WeapAnim.IDLE || a === WeapAnim.EMPTY_IDLE;
}

/** The slots/clips part of a vm_*.json. */
export interface VmAnimInfo {
  slots: Readonly<Record<string, string>>;
  clips: Readonly<Record<string, { duration: number; looped: boolean; notetracks?: readonly { name: string; time: number }[] }>>;
}

/** Case-insensitive clip lookup (weapon files spell viewmodel_M4m203_*, the assets are lower case). */
export function findClipName(info: VmAnimInfo, clip: string | undefined): string | null {
  if (!clip) return null;
  if (info.clips[clip]) return clip;
  const lc = clip.toLowerCase();
  for (const k of Object.keys(info.clips)) if (k.toLowerCase() === lc) return k;
  return null;
}

/**
 * Resolve a WeapAnim to a clip name for one viewmodel. `clipEmpty` selects the *_empty crawl/dtp variants.
 * Returns null only when the weapon has no idle either (never for the SnS set).
 */
export function resolveClip(info: VmAnimInfo, anim: number, clipEmpty: boolean): { clip: string; anim: number } | null {
  let a = anim & WEAPANIM_MASK;
  for (let guard = 0; guard < 6; guard++) {
    let slot = WEAPANIM_SLOT[a];
    if (slot && clipEmpty && EMPTY_VARIANT[slot] && info.slots[EMPTY_VARIANT[slot]]) slot = EMPTY_VARIANT[slot];
    const clip = slot ? findClipName(info, info.slots[slot]) : null;
    if (clip) return { clip, anim: a };
    const fb = FALLBACK[a];
    if (fb !== undefined) { a = fb; continue; }
    if (a === WeapAnim.IDLE) return null;
    a = WeapAnim.IDLE;
  }
  return null;
}

function rawMs(def: WeaponDef, key: string): number {
  const v = def.raw[key];
  return typeof v === 'number' ? Math.round(v * 1000) : 0;
}

/** Weapon-state time (ms) an anim is stretched to; null = plays at its own rate. */
export function animStateTimeMs(def: WeaponDef, anim: number): number | null {
  switch (anim & WEAPANIM_MASK) {
    case WeapAnim.HOLD_FIRE: return def.holdFireTimeMs;
    case WeapAnim.MELEE: return def.meleeTimeMs;
    case WeapAnim.MELEE_CHARGE: return def.meleeChargeTimeMs;
    case WeapAnim.RELOAD: return def.reloadTimeMs;
    case WeapAnim.RELOAD_EMPTY: return def.reloadEmptyTimeMs;
    case WeapAnim.RAISE: return def.raiseTimeMs;
    case WeapAnim.FIRST_RAISE: return def.firstRaiseTimeMs;
    case WeapAnim.DROP: return def.dropTimeMs;
    case WeapAnim.ALT_RAISE: return rawMs(def, 'altRaiseTime');
    case WeapAnim.ALT_DROP: return rawMs(def, 'altDropTime');
    case WeapAnim.QUICK_RAISE: return def.quickRaiseTimeMs;
    case WeapAnim.QUICK_DROP: return def.quickDropTimeMs;
    case WeapAnim.EMPTY_RAISE: return def.emptyRaiseTimeMs;
    case WeapAnim.EMPTY_DROP: return def.emptyDropTimeMs;
    case WeapAnim.SPRINT_IN: case WeapAnim.SPRINT_IN_EMPTY: return def.sprintInTimeMs;
    case WeapAnim.SPRINT_LOOP: case WeapAnim.SPRINT_LOOP_EMPTY: return def.sprintLoopTimeMs;
    case WeapAnim.SPRINT_OUT: case WeapAnim.SPRINT_OUT_EMPTY: return def.sprintOutTimeMs;
    case WeapAnim.DTP_IN: return def.dtpInTimeMs;
    case WeapAnim.DTP_LOOP: return def.dtpLoopTimeMs;
    case WeapAnim.DTP_OUT: return def.dtpOutTimeMs;
    case WeapAnim.CRAWL_IN: return rawMs(def, 'crawlInTime');
    case WeapAnim.CRAWL_FORWARD: return rawMs(def, 'crawlForwardTime');
    case WeapAnim.CRAWL_BACK: return rawMs(def, 'crawlBackTime');
    case WeapAnim.CRAWL_RIGHT: return rawMs(def, 'crawlRightTime');
    case WeapAnim.CRAWL_LEFT: return rawMs(def, 'crawlLeftTime');
    case WeapAnim.CRAWL_OUT: return rawMs(def, 'crawlOutTime');
    default: return null; // IDLE, EMPTY_IDLE, FIRE, LAST_SHOT, ADS_FIRE, ADS_LAST_SHOT, ADS_UP/DOWN
  }
}

/**
 * GetWeaponAnimRate: clip seconds / state seconds. A zero state time gives rate 0 (engine returns 0.0: the
 * anim holds its first frame) — except for the crawl family (ESTIMATE, T6-only) where 0 means "native".
 */
export function animRate(def: WeaponDef, anim: number, clipDurationS: number): number {
  const t = animStateTimeMs(def, anim);
  if (t === null) return 1;
  if (t <= 0) {
    const a = anim & WEAPANIM_MASK;
    return a >= WeapAnim.CRAWL_IN && a <= WeapAnim.CRAWL_OUT ? 1 : 0;
  }
  return (clipDurationS * 1000) / t;
}

/**
 * GetWeaponAnimTimeFrac (used when the viewmodel starts mid-state, e.g. first frame after a weapon/rig change):
 * fraction already elapsed = (stateTime - weaponTime) / stateTime for the rate-scaled raise/drop/reload anims.
 */
export function animStartFrac(def: WeaponDef, anim: number, weaponTimeMs: number): number {
  const a = anim & WEAPANIM_MASK;
  // T5 GetWeaponAnimTimeFrac only resumes the reload and raise/drop families (anim indices 9..24)
  if (!START_FRAC_ANIMS.has(a)) return 0;
  const t = animStateTimeMs(def, a);
  if (!t || t <= 0) return 0;
  const f = (t - weaponTimeMs) / t;
  return f < 0 ? 0 : f > 1 ? 1 : f;
}
const START_FRAC_ANIMS = new Set<number>([
  WeapAnim.RELOAD, WeapAnim.RELOAD_EMPTY, WeapAnim.RAISE, WeapAnim.FIRST_RAISE, WeapAnim.DROP, WeapAnim.ALT_RAISE,
  WeapAnim.ALT_DROP, WeapAnim.QUICK_RAISE, WeapAnim.QUICK_DROP, WeapAnim.EMPTY_RAISE, WeapAnim.EMPTY_DROP,
]);

/** Offhand states (T5 WEAPON_OFFHAND_INIT..WEAPON_OFFHAND_END = ours OFFHAND_INIT..OFFHAND_END). */
export function isOffhandState(s: number): boolean {
  return s >= WeaponState.OFFHAND_INIT && s <= WeaponState.OFFHAND_END;
}

/**
 * States in which BG_GetViewmodelWeaponIndex shows the offhand: T5 requires (weapFlags & 2), which is set on
 * entering WEAPON_OFFHAND_PREPARE / OFFHAND_START (bg_weapons.cpp:3875-4010) and cleared by PM_Weapon_OffHandEnd
 * (bg_weapons.cpp:4022-4054, `weapFlags &= ~2` as it enters OFFHAND_END). So OFFHAND_INIT (the held weapon's quick
 * drop) and OFFHAND_END (the held weapon's quick raise / pullout_quick) show ps.weapon, not the hatchet. Same
 * rule as shared/weapons/pm_weapon.ts viewmodelWeapon().
 */
export function isOffhandViewmodelState(s: number): boolean {
  return s >= WeaponState.OFFHAND_PREPARE && s <= WeaponState.OFFHAND;
}

/** BG_GetViewmodelWeaponIndex (T5 bg_weapons.cpp:220). */
export function viewmodelWeapon(ps: Pick<PlayerState, 'weapon' | 'weaponState' | 'offHandIndex' | 'meleeWeapon'>, useAsMelee: (w: WeaponId) => boolean): WeaponId {
  if (isOffhandViewmodelState(ps.weaponState) && ps.offHandIndex !== WeaponId.NONE) return ps.offHandIndex;
  if ((ps.weaponState === WeaponState.MELEE_INIT || ps.weaponState === WeaponState.MELEE_FIRE) && !useAsMelee(ps.weapon)
    && ps.meleeWeapon !== WeaponId.NONE) return ps.meleeWeapon;
  return ps.weapon;
}

const RELOAD_STATES = new Set<number>([
  WeaponState.RELOADING, WeaponState.RELOADING_INTERUPT, WeaponState.RELOAD_START, WeaponState.RELOAD_START_INTERUPT, WeaponState.RELOAD_END,
]);
export function isReloadState(s: number): boolean {
  return RELOAD_STATES.has(s);
}

/** ViewmodelRocketShouldBeAttached: blade shown when loaded, or during a reload once showRocketTime passed. */
export function rocketVisible(def: WeaponDef, ps: Pick<PlayerState, 'weapon' | 'weaponState' | 'weaponTime' | 'ammoClip'>): boolean {
  if ((ps.ammoClip[ps.weapon] ?? 0) > 0) return true;
  return isReloadState(ps.weaponState) && def.reloadTimeMs - ps.weaponTime > def.reloadShowRocketTimeMs;
}

/** All WeapAnim ids (for tests). */
export const ALL_WEAPANIMS: readonly number[] = Object.values(WeapAnim);
